import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { purgeDeviceData } from "@/lib/deviceCleanup";
import { outboxCount } from "@/lib/offline";
import {
  isStandalonePWA, idleLimitMs, idleWarnMs,
  readLastActivity, writeLastActivity, isIdleExpired, markIdleSignout,
} from "@/lib/idle";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
const WRITE_THROTTLE_MS = 5000;
const CHECK_EVERY_MS = 5000;

// Signs a BROWSER-TAB user out after real inactivity (with a 1-minute warning).
// Never active in the installed PWA — that keeps its persistent session.
export const IdleGuard = () => {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [warn, setWarn] = useState(false);
  const [unsent, setUnsent] = useState(0);
  const lastRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const signingOut = useRef(false);
  const uid = session?.user?.id;
  const enabled = Boolean(session?.user) && !isStandalonePWA();

  const idleSignOut = useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;
    setWarn(false);
    markIdleSignout();
    const queued = uid ? await outboxCount(uid) : 0;
    await signOut();
    // Step 11 scenario 8: an automatic sign-out must never destroy unsent
    // field work without the user confirming — keep the queue on device.
    // The user-switch cleanup still wipes it if a different user signs in.
    if (queued === 0) await purgeDeviceData();
    navigate("/login", { replace: true });
    signingOut.current = false;
  }, [uid, signOut, navigate]);

  const bump = useCallback(() => {
    const now = Date.now();
    lastRef.current = now;
    if (now - lastWriteRef.current > WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeLastActivity(now);
    }
    setWarn((w) => (w ? false : w));
  }, []);

  const staySignedIn = useCallback(() => {
    lastWriteRef.current = 0;
    bump();
  }, [bump]);

  useEffect(() => {
    if (!enabled) { setWarn(false); return; }

    // Resume from the persisted timestamp so the limit survives a browser
    // close; a plain page refresh within the window stays signed in.
    const stored = readLastActivity();
    if (stored !== null && Date.now() - stored >= idleLimitMs()) { idleSignOut(); return; }
    lastRef.current = stored ?? Date.now();
    if (stored === null) writeLastActivity(lastRef.current);

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true, capture: true }));

    const check = () => {
      // Trust the persisted timestamp (another tab may be active too).
      const last = Math.max(lastRef.current, readLastActivity() ?? 0);
      lastRef.current = last;
      const idle = Date.now() - last;
      if (idle >= idleLimitMs()) idleSignOut();
      else setWarn(idle >= idleLimitMs() - idleWarnMs());
    };
    const interval = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump, { capture: true }));
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled, bump, idleSignOut]);

  // Surface unsent-work count inside the idle warning (Step 11 scenario 8).
  useEffect(() => {
    if (warn && uid) outboxCount(uid).then(setUnsent).catch(() => setUnsent(0));
    else setUnsent(0);
  }, [warn, uid]);

  if (!enabled || !warn) return null;

  return (
    <Box position="fixed" left="0" right="0" bottom="0" zIndex={2147483300} p="4" pointerEvents="none">
      <Flex role="alertdialog" aria-label="Inactivity warning" pointerEvents="auto"
        maxW="440px" mx="auto" align="center" gap="3" px="4" py="3"
        bg="#1F2937" color="#FFFFFF" borderRadius="14px"
        boxShadow="0 12px 32px rgba(31,41,55,0.35)" data-testid="idle-warning">
        <Clock size={18} color="#F4B860" style={{ flexShrink: 0 }} />
        <Text fontSize="sm" flex="1" data-testid="idle-warning-text">
          You'll be signed out soon due to inactivity.
          {unsent > 0 && ` You have ${unsent} unsent form${unsent === 1 ? "" : "s"} — ${unsent === 1 ? "it" : "they"} will stay saved on this device.`}
        </Text>
        <Button size="sm" onClick={staySignedIn} data-testid="stay-signed-in-btn">Stay signed in</Button>
      </Flex>
    </Box>
  );
};
